import React from "react";
import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';


const COOKIE_PREFIX = 'warning_suppress_';

function getSuppressCookieName(id: string): string {
    return `${COOKIE_PREFIX}${id}`;
}

function isSuppressed(id: string): boolean {
    const name = getSuppressCookieName(id);
    return document.cookie.split(';').some(c => c.trim().startsWith(`${name}=`));
}

function suppress(id: string, days: number): void {
    const name = getSuppressCookieName(id);
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    document.cookie = `${name}=1; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}


const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.modal + 1,
    },
    overlay: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
    rootWarning: {
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        maxWidth: '500px',
        width: '100%',
    },
    rootInfo: {
        borderLeft: `4px solid ${theme.palette.info.main}`,
        maxWidth: '500px',
        width: '100%',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
    },
    warningIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.warning.main,
    },
    infoIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.info.main,
    },
}));

interface IProps {
    /** Unique identifier used as cookie key for suppression */
    id: string;
    /** Message to display */
    message: string;
    /** When true, renders as an info message instead of a warning */
    info?: boolean;
    /** When true, shows a "Don't show again" button */
    suppressible?: boolean;
    /** Number of days to suppress the warning when dismissed with "Don't show again" */
    suppressDays?: number;
}

export type AllProps = IProps;

export const WarningMessage = (props: AllProps) => {
    const { suppressible = false, suppressDays = 30, info = false } = props;
    const theme = useTheme();
    const classes = useStyles(theme);
    const { t: tCommon } = useTranslation('common');

    const [visible, setVisible] = React.useState<boolean>(() => !isSuppressed(props.id));
    const cardRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!visible) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
                setVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [visible]);

    if (!visible) {
        return null;
    }

    const handleDismiss = () => setVisible(false);

    const handleSuppress = () => {
        suppress(props.id, suppressDays);
        setVisible(false);
    };

    return (
        <Backdrop open className={classes.backdrop}>
            <div className={classes.overlay}>
                <Card ref={cardRef} className={info ? classes.rootInfo : classes.rootWarning} variant="outlined">
                    <CardContent>
                        <div className={classes.header}>
                            {info
                                ? <InfoIcon fontSize="small" className={classes.infoIcon} />
                                : <WarningIcon fontSize="small" className={classes.warningIcon} />
                            }
                            <Typography variant="body2" color="textPrimary">
                                {props.message}
                            </Typography>
                        </div>
                    </CardContent>
                    <CardActions>
                        <Button size="small" color="primary" onClick={handleDismiss}>
                            {tCommon('close')}
                        </Button>
                        {suppressible && (
                            <Button size="small" onClick={handleSuppress}>
                                {tCommon('dontShowAgainForDays', { days: suppressDays })}
                            </Button>
                        )}
                    </CardActions>
                </Card>
            </div>
        </Backdrop>
    );
};

export default WarningMessage;
